import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Input, InputGroup, Label } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { updateOperator, getOperators } from '../../slices/operators/thunk';

const StopLossModal = ({ isOpen, toggle, operator }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [stopLoss, setStopLoss] = useState({ lt: '', lt1: '', lt2: '', ro: '', so: '' });

    const presets = [500, 1000, 2000, 5000, 10000];

    useEffect(() => {
        if (isOpen && operator) {
            setStopLoss({
                lt: operator.stopLoss?.lt ?? '',
                lt1: operator.stopLoss?.lt1 ?? '',
                lt2: operator.stopLoss?.lt2 ?? operator.stopLoss?.lt3 ?? '',
                ro: operator.stopLoss?.ro ?? '',
                so: operator.stopLoss?.so ?? ''
            });
        }
    }, [isOpen, operator]);

    const handleSave = async () => {
        if (!operator) return;
        setLoading(true);
        try {
            const lt2Val = stopLoss.lt2 === '' || stopLoss.lt2 === null ? null : Number(stopLoss.lt2);
            const payloadData = {
                stopLoss: {
                    lt: stopLoss.lt === '' || stopLoss.lt === null ? null : Number(stopLoss.lt),
                    lt1: stopLoss.lt1 === '' || stopLoss.lt1 === null ? null : Number(stopLoss.lt1),
                    lt2: lt2Val,
                    lt3: lt2Val,
                    ro: stopLoss.ro === '' || stopLoss.ro === null ? null : Number(stopLoss.ro),
                    so: stopLoss.so === '' || stopLoss.so === null ? null : Number(stopLoss.so)
                }
            };

            const result = await dispatch(updateOperator({ id: operator._id, data: payloadData }));
            if (result.success || (result.payload && (result.payload.success || result.payload.status === 1))) {
                dispatch(getOperators({ page: 1, limit: 20 }));
                toggle();
            }
        } catch (error) {
            console.error("Error updating operator stopLoss:", error);
        } finally {
            setLoading(false);
        }
    };

    const renderGameStopLoss = (gameKey, label, badgeColor) => {
        const currentValue = stopLoss[gameKey];
        const numVal = currentValue !== '' && currentValue !== null ? Number(currentValue) : null;

        return (
            <div className="game-settings-section mb-3 p-3 rounded bg-light border" key={gameKey}>
                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <h6 className="mb-0 text-primary d-flex align-items-center fs-14 fw-bold">
                        <i className="ri-gamepad-line me-2"></i> {label}
                    </h6>
                    {numVal !== null && (
                        <Button
                            color="none"
                            className="btn-link p-0 text-danger text-decoration-none fs-11"
                            onClick={() => setStopLoss(prev => ({ ...prev, [gameKey]: '' }))}
                        >
                            <i className="ri-close-circle-line me-1"></i> Clear (No Limit)
                        </Button>
                    )}
                </div>

                <div className="mb-2">
                    <Label className="fw-bold text-uppercase fs-11 text-muted mb-2">Quick Presets</Label>
                    <div className="d-flex flex-nowrap overflow-auto gap-1 pb-1 align-items-center">
                        {presets.map((amt) => (
                            <button
                                key={amt}
                                type="button"
                                className={`stoploss-btn ${numVal === amt ? 'active' : ''}`}
                                onClick={() => setStopLoss(prev => ({ ...prev, [gameKey]: amt }))}
                                disabled={loading}
                            >
                                {amt.toLocaleString()}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`stoploss-btn ${numVal === 0 ? 'active' : ''}`}
                            style={numVal === 0 ? {} : { borderColor: '#f06548', color: '#f06548' }}
                            onClick={() => setStopLoss(prev => ({ ...prev, [gameKey]: 0 }))}
                            disabled={loading}
                        >
                            0 (No Loss)
                        </button>
                    </div>
                </div>

                <div className="mt-2">
                    <Label className="fw-bold text-uppercase fs-11 text-muted mb-1">Custom Limit</Label>
                    <InputGroup size="sm">
                        <span className="input-group-text bg-white border-end-0 text-muted fs-12">
                            <i className="ri-money-dollar-circle-line"></i>
                        </span>
                        <Input
                            type="number"
                            placeholder="Enter custom stop loss amount e.g. 1500"
                            value={currentValue}
                            onChange={(e) => setStopLoss(prev => ({ ...prev, [gameKey]: e.target.value }))}
                            style={{ fontSize: '13px' }}
                        />
                    </InputGroup>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" centered className="stoploss-modal">
            <ModalHeader toggle={toggle} className="bg-light py-2">
                <span className="fw-bold fs-15 text-dark">
                    <i className="ri-shield-flash-line text-primary me-2 align-middle fs-18"></i>
                    Set Operator Dynamic Stop Loss
                </span>
            </ModalHeader>
            <ModalBody className="p-3" style={{ maxHeight: 'calc(90vh - 110px)', overflowY: 'auto' }}>
                <style>{`
                    .stoploss-modal .modal-content {
                        max-height: 90vh;
                        border-radius: 10px;
                        overflow: hidden;
                    }
                    .stoploss-btn {
                        padding: 5px 9px;
                        border-radius: 6px;
                        border: 2px solid #dee2e6;
                        background-color: #ffffff;
                        color: #495057;
                        font-weight: 600;
                        font-size: 11px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        min-width: 46px;
                        white-space: nowrap;
                    }
                    .stoploss-btn:hover {
                        border-color: #0d9582;
                        background-color: #0d9582;
                        color: #ffffff;
                    }
                    .stoploss-btn.active {
                        background-color: #0d9582;
                        border-color: #0d9582;
                        color: #ffffff;
                        box-shadow: 0 2px 4px rgba(13, 149, 130, 0.25);
                    }
                    .stoploss-modal .modal-header .btn-close {
                        padding: 0.5rem 0.5rem;
                        margin: -0.5rem -0.5rem -0.5rem auto;
                    }
                `}</style>

                {/* Operator Profile Banner */}
                <div className="text-center mb-3 p-2 bg-primary-subtle rounded border border-primary-subtle">
                    <h6 className="mb-0 text-primary fs-13 fw-bold">
                        Account: {operator?.acnumber || operator?.username}
                    </h6>
                    <p className="text-muted small mb-0 fs-11">
                        Username: <span className="fw-bold text-dark">{operator?.username}</span> &nbsp;|&nbsp;
                        Role: <span className="text-uppercase fw-bold text-success">Operator</span>
                    </p>
                </div>

                {/* Priority Rule Alert */}
                <div className="alert alert-info py-2 px-3 fs-12 mb-3 d-flex align-items-center rounded border-info-subtle">
                    <i className="ri-information-line me-2 fs-16 text-info flex-shrink-0"></i>
                    <div>
                        <strong>1st Priority Rule:</strong> The engine caps maximum net payout loss per round across all users under this operator to the configured stop loss limit.
                    </div>
                </div>

                {/* Game Sections */}
                {renderGameStopLoss('lt', 'Lucky Target (LT)', 'primary')}
                {renderGameStopLoss('lt1', 'Lucky Target 1 (LT1)', 'info')}
                {renderGameStopLoss('lt2', 'Lucky Target 2 (LT2)', 'success')}
                {renderGameStopLoss('ro', 'American Roulette (RO)', 'warning')}
                {renderGameStopLoss('so', 'Sorat (SO)', 'danger')}
            </ModalBody>
            <ModalFooter className="p-2 px-3 bg-light border-top d-flex justify-content-end gap-2">
                <Button color="light" size="sm" onClick={toggle} disabled={loading} className="px-3">
                    Cancel
                </Button>
                <Button
                    color="success"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                    className="px-3 fw-bold"
                    style={{ backgroundColor: '#0d9582', borderColor: '#0d9582' }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="ri-save-line me-1"></i> Save Stop Loss
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default StopLossModal;
